import React from "react";
import { Link } from "react-router-dom";
import { Lock, Mail, Phone, MapPin, ArrowLeft } from "lucide-react";
import { BRAND } from "@/lib/brand";
import useSeo from "@/lib/useSeo";

export default function PrivacyPage() {
  useSeo({
    title: "Privacy Policy — Top-Notch Pro Solutions",
    description: "Privacy Policy for Top-Notch Pro Solutions. Last updated June 18, 2026.",
  });
  return (
    <div data-testid="privacy-page" className="max-w-3xl mx-auto px-6 lg:px-8 py-12 md:py-20">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[var(--brand-teal)] mb-6" data-testid="privacy-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-[var(--brand-teal)] text-white flex items-center justify-center">
          <Lock className="w-5 h-5" />
        </div>
        <span className="section-eyebrow">Legal</span>
      </div>
      <h1 className="heading-display text-4xl md:text-5xl">Privacy Policy</h1>
      <p className="mt-3 text-sm text-slate-500"><strong>Last Updated:</strong> June 18, 2026</p>

      <div className="mt-10 space-y-8 text-slate-700 leading-relaxed">
        <p>
          At <strong>{BRAND.legalName}</strong>, we respect your privacy and are committed to protecting the
          personal information you share with us. This Privacy Policy explains how we collect, use, and safeguard your
          data when you visit our website.
        </p>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>1. Information We Collect</h2>
          <p>
            We collect personal information that you voluntarily provide to us when you book a cleaning service,
            request a quote, or contact us. This may include:
          </p>
          <ul className="mt-3 space-y-2 list-disc pl-5">
            <li>Name, email address, phone number, and physical address.</li>
            <li>Project details, descriptions, and uploaded photos of the work area.</li>
            <li>Payment information (processed securely through our third-party booking partners).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>2. Age Restriction</h2>
          <p>
            Our website and services are strictly intended for users who are 18 years of age or older. We do not
            knowingly collect personal data from anyone under 18. If we learn we have collected info from a minor, we
            will delete it immediately.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="mt-3 space-y-2 list-disc pl-5">
            <li>Provide, operate, and maintain our website.</li>
            <li>Process your instant cleaning bookings and generate custom project quotes.</li>
            <li>Communicate with you regarding your service requests, appointments, or inquiries.</li>
            <li>Improve our customer service and website experience.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>4. Data Sharing and Protection</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We may share data with trusted
            third-party service providers (such as payment processors or booking software) solely to fulfill your
            requested services. We implement industry-standard security measures to protect your data.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>5. Your Rights</h2>
          <p>
            Depending on your location, you may have the right to access, correct, or request the deletion of the
            personal information we hold about you. To exercise these rights, please contact us using the details
            below.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>6. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or how we handle your data, please contact us at:
          </p>
          <div className="mt-4 rounded-xl bg-[var(--brand-cream-soft)] p-5 space-y-2">
            <a href="mailto:topnotchprosolutions@gmail.com" className="flex items-center gap-2 text-[var(--brand-teal)] font-semibold hover:underline">
              <Mail className="w-4 h-4 text-[var(--brand-gold)]" /> topnotchprosolutions@gmail.com
            </a>
            <a href={BRAND.phoneHref} className="flex items-center gap-2 text-[var(--brand-teal)] font-semibold hover:underline">
              <Phone className="w-4 h-4 text-[var(--brand-gold)]" /> {BRAND.phone}
            </a>
            <p className="flex items-center gap-2 text-slate-700">
              <MapPin className="w-4 h-4 text-[var(--brand-gold)]" /> Evansville, Indiana
            </p>
          </div>
        </section>
      </div>

      <div className="mt-12 text-center text-xs text-slate-500">
        <Link to="/terms" className="text-[var(--brand-teal)] hover:underline">View our Terms & Conditions →</Link>
      </div>
    </div>
  );
}
